import { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import {
  fetchIndicatorsData,
} from "../../Utils/IndicatorsUtils";
import useApi from "../../Hooks/useApi";
import { isAuthenticated } from "../../Helpers/authHelper";
import { useTranslation } from "react-i18next";

export default function ChartComponent({ selectedIndicator, year }) {
  const chartRef = useRef(null);
  const tooltipRef = useRef(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { request } = useApi({ isPublic: !isAuthenticated() });
  const { t } = useTranslation();

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedIndicator) {
        setChartData([]);
        return;
      }

      try {
        setLoading(true);
        const res = await fetchIndicatorsData(request, selectedIndicator, year);
        if (res?.features?.length) {
          const processed = res.features
            .map(f => ({
              city: f.properties.name || f.properties.city,
              value: Number(f.properties.renderdata)
            }))
            .filter(d => typeof d.value === 'number' && !isNaN(d.value))
            .sort((a, b) => a.value - b.value);
          setChartData(processed);
        } else {
          setChartData([]);
        }
      } catch (err) {
        console.error("Failed to fetch chart data:", err);
        setChartData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedIndicator, year]);

  useEffect(() => {
    if (!chartRef.current) return;

    const margin = { top: 30, right: 30, bottom: 150, left: 60 };
    const width = chartRef.current.clientWidth - margin.left - margin.right;
    const height = chartRef.current.clientHeight - margin.top - margin.bottom;

    const svg = d3.select(chartRef.current)
      .selectAll("g.main")
      .data([0])
      .join("g")
      .attr("class", "main")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Initialize tooltip once
    if (!tooltipRef.current) {
      tooltipRef.current = d3.select("body")
        .append("div")
        .attr("class", "tooltip")
        .style("position", "absolute")
        .style("background", "#333")
        .style("color", "#fff")
        .style("padding", "6px 10px")
        .style("border-radius", "5px")
        .style("font-size", "12px")
        .style("pointer-events", "none")
        .style("opacity", 0);
    }

    // Fallback placeholder if data is missing
    const cities = chartData.length ? chartData.map(d => d.city) : ["City 1", "City 2", "City 3", "City 4", "City 5"];
    const values = chartData.length ? chartData.map(d => d.value) : [0, 25, 50, 75, 100];

    const x = d3.scaleBand()
      .domain(cities)
      .range([0, width])
      .padding(0.1);

    const y = d3.scaleLinear()
      .domain([0, d3.max(values)])
      .nice()
      .range([height, 0]);
    // Remove old grid
    svg.selectAll(".grid").remove();

    // Add horizontal grid lines
    const grid = svg.append("g")
      .attr("class", "grid")
      .call(
        d3.axisLeft(y)
          .tickSize(-width)
          .tickFormat("")
      );

    // Style grid lines
    grid.selectAll("line")
      .attr("stroke", "#ddd")
      .attr("stroke-dasharray", "3,3");

    // Remove topmost grid line (at 100%)
    const gridLines = grid.selectAll("line").nodes();
    if (gridLines.length > 0) {
      d3.select(gridLines[0]).remove(); // This is the topmost line (highest tick)
    }

    // Optionally remove domain line (axis path)
    grid.select(".domain").remove();

    // Render Y Axis
    svg.selectAll(".y-axis").data([0])
      .join(
        enter => enter.append("g").attr("class", "y-axis"),
        update => update.transition().duration(1000)
      )
      .call(d3.axisLeft(y).tickFormat(d => `${d}%`));

    // Render X Axis
    svg.selectAll(".x-axis").data([0])
      .join(
        enter => enter.append("g").attr("class", "x-axis"),
        update => update.transition().duration(1000)
      )
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(x))
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end")
      .attr("dx", "-0.8em")
      .attr("dy", "0.15em");

    // Bar color scale
    const colorScale = d3.scaleLinear()
      .domain(d3.extent(chartData, d => d.value))
      .range(["#e74c3c", "#2ecc71"]);

    // Draw bars only if real data exists
    const bars = svg.selectAll(".bar")
      .data(chartData, d => d.city);

    bars.exit()
      .transition()
      .duration(500)
      .attr("y", height)
      .attr("height", 0)
      .remove();

    bars.enter()
      .append("rect")
      .attr("class", "bar")
      .attr("x", d => x(d.city))
      .attr("width", x.bandwidth())
      .attr("y", height)
      .attr("height", 0)
      .attr("fill", d => colorScale(d.value))
      .attr("rx", 2)
      .attr("ry", 2)
      .on("mouseover", function (event, d) {
        tooltipRef.current
          .style("opacity", 1)
          .html(`<strong>${d.city}</strong><br/>Value: ${d.value}%`);
        d3.select(this).attr("opacity", 0.7);
      })
      .on("mousemove", function (event) {
        const tooltip = tooltipRef.current;
        const tooltipWidth = tooltip.node().offsetWidth;
        const tooltipHeight = tooltip.node().offsetHeight;

        let left = event.pageX + 10;
        let top = event.pageY - 28;

        if (left + tooltipWidth > window.innerWidth - 20) {
          left = event.pageX - tooltipWidth - 20;
        }
        left = Math.max(10, Math.min(left, window.innerWidth - tooltipWidth - 10));

        if (top + tooltipHeight > window.innerHeight - 20) {
          top = window.innerHeight - tooltipHeight - 20;
        }
        top = Math.max(10, top);

        tooltip
          .style("left", `${left}px`)
          .style("top", `${top}px`);
      })
      .on("mouseout", function () {
        tooltipRef.current.style("opacity", 0);
        d3.select(this).attr("opacity", 1);
      })
      .transition()
      .duration(1000)
      .attr("y", d => y(d.value))
      .attr("height", d => height - y(d.value));

    bars.merge(bars)
      .transition()
      .duration(1000)
      .attr("x", d => x(d.city))
      .attr("width", x.bandwidth())
      .attr("y", d => y(d.value))
      .attr("height", d => height - y(d.value))
      .attr("fill", d => colorScale(d.value));

    // Chart Title
    svg.selectAll(".chart-title").data([0])
      .join(
        enter => enter.append("text").attr("class", "chart-title"),
        update => update.transition().duration(1000)
      )
    // .attr("x", width / 2)
    // .attr("y", -10)
    // .attr("text-anchor", "middle")
    // .style("font-size", "16px")
    // .style("font-weight", "bold")
    // .text(
    //   selectedIndicator?.name
    //     ? `Data for ${selectedIndicator.name}`
    //     : "No Indicator Selected"
    // );

  }, [chartData, selectedIndicator]);

  const overlayMessage = !selectedIndicator
    ? t("indicators.select_indicator_please")
    : !loading && chartData.length === 0
    ? t("indicators.no_data_found")
    : null;

  return (
    <div className="w-full h-full bg-white relative">
      {overlayMessage && (
        <div className="absolute top-1/2 left-1/2 z-[1000] -translate-x-1/2 -translate-y-1/2 bg-white/90 px-6 py-4 rounded-lg shadow-lg text-center pointer-events-none">
          <p className="m-0 font-bold text-sm text-gray-600">
            {overlayMessage}
          </p>
        </div>
      )}
      <svg ref={chartRef} width="100%" height="100%"></svg>
    </div>
  );
}
